import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck2, Shield, Terminal, Lock, ArrowLeft } from "lucide-react";
import { AuthWheelShowcase } from "./AuthWheelShowcase";
import { AdminPortalEasterEgg } from "./AdminPortalEasterEgg";
import { APP_LOGIN } from "../lib/authRouting";
import { LOGO_GREEN } from "../lib/brandColors";
import { SiteAnnouncementBar } from "./SiteAnnouncementBar";
import { BetaBadge } from "./BetaBadge";
import { SiteFooter } from "./SiteFooter";

function FairShareLogo({ className = "h-7 w-7 shrink-0", accent = LOGO_GREEN }) {
    return (
        <svg viewBox="0 0 32 32" className={className} aria-hidden>
            <circle cx="16" cy="16" r="13" stroke={accent} strokeWidth="2" fill="none" />
            <path d="M16 3 A13 13 0 0 1 27.25 22.5 L16 16 Z" fill={accent} opacity="0.95" />
        </svg>
    );
}

function BrandMark({ variant = "app", compact = false }) {
    const isAdmin = variant === "admin";
    const accent = isAdmin ? "#5d00ff" : LOGO_GREEN;

    return (
        <div className={`auth-shell__brand ${compact ? "auth-shell__brand--compact" : ""}`}>
            {isAdmin ? <Shield className="auth-shell__admin-shield h-6 w-6 shrink-0" aria-hidden /> : <FairShareLogo accent={accent} />}
            <span className="font-heading text-xl tracking-tight">
                <span className="text-white">Fair</span>
                <span style={{ color: accent }}>Share</span>
            </span>
            {!isAdmin ? <BetaBadge /> : null}
            {isAdmin ? <span className="auth-shell__badge auth-shell__badge--admin">Console</span> : null}
        </div>
    );
}

const ADMIN_OPS = [
    {
        icon: Terminal,
        title: "Platform operations",
        desc: "Users, orgs, roles, cross-tenant controls",
    },
    {
        icon: ShieldCheck,
        title: "Audit & compliance",
        desc: "Immutable platform-level audit trail",
    },
    {
        icon: Lock,
        title: "Full system access",
        desc: "Authenticated administrators only",
    },
];

function AppMarketingPanel() {
    return (
        <div className="auth-shell__marketing-stack">
            <Link to="/" className="auth-shell__marketing-logo" data-testid="auth-marketing-home">
                <FairShareLogo />
                <span className="font-heading text-xl tracking-tight">
                    <span className="text-white">Fair</span>
                    <span style={{ color: LOGO_GREEN }}>Share</span>
                </span>
            </Link>

            <div className="auth-shell__marketing-hero">
                <AuthWheelShowcase />

                <div className="auth-shell__marketing-copy">
                    <p className="label-eyebrow text-[#00BF0D]">Profit-sharing platform</p>
                    <h2 className="auth-shell__marketing-headline text-balance">
                        Split revenue with your team.{" "}
                        <span className="text-[#00BF0D]">Automated. Audited. Calm.</span>
                    </h2>
                </div>
            </div>

            <div className="auth-shell__feature-grid">
                <div className="auth-shell__feature-card">
                    <ShieldCheck className="auth-shell__feature-icon" aria-hidden />
                    <p className="auth-shell__feature-title">Bank-grade encryption</p>
                    <p className="auth-shell__feature-desc">AES-256 at rest · TLS in transit</p>
                </div>
                <div className="auth-shell__feature-card">
                    <FileCheck2 className="auth-shell__feature-icon" aria-hidden />
                    <p className="auth-shell__feature-title">Immutable ledger</p>
                    <p className="auth-shell__feature-desc">Append-only payout history</p>
                </div>
            </div>

            <p className="auth-shell__version font-mono">v2.4 · audited</p>
        </div>
    );
}

function AdminAuthBackground() {
    return (
        <div className="admin-auth-bg" aria-hidden>
            <div className="admin-auth-bg__grid grid-bg" />
            <div className="admin-auth-bg__orb admin-auth-bg__orb--a" />
            <div className="admin-auth-bg__orb admin-auth-bg__orb--b" />
            <div className="admin-auth-bg__orb admin-auth-bg__orb--c" />
            <div className="admin-auth-bg__vignette" />
        </div>
    );
}

function AdminAuthShell({ title, subtitle, children, loginPage = false }) {
    return (
        <div
            className={`auth-shell auth-shell--admin auth-route-root${loginPage ? " auth-route-login" : ""}`}
            data-testid="auth-shell-admin"
        >
            <AdminAuthBackground />

            <div className="admin-auth-stage">
                <div className="admin-auth-card">
                    <div className="admin-auth-card__shine" aria-hidden />

                    <header className="admin-auth-header">
                        <BrandMark variant="admin" />
                        <p className="admin-auth-eyebrow label-eyebrow">Restricted access</p>
                        <h1 className="admin-auth-title">Superadmin console</h1>
                        <p className="admin-auth-lead">
                            Platform operations, org controls, and immutable audit — privileged session only.
                        </p>
                    </header>

                    <div className="admin-auth-ops">
                        {ADMIN_OPS.map(({ icon: Icon, title: opTitle, desc }) => (
                            <div key={opTitle} className="admin-auth-ops-item">
                                <div className="admin-auth-ops-icon-wrap">
                                    <Icon className="admin-auth-ops-icon" aria-hidden />
                                </div>
                                <p className="admin-auth-ops-title">{opTitle}</p>
                                <p className="admin-auth-ops-desc">{desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="admin-auth-divider" aria-hidden />

                    <div className="admin-auth-form-block">
                        <header className="admin-auth-form-header">
                            <h2 className="admin-auth-form-title">{title}</h2>
                            <p className="admin-auth-form-subtitle">{subtitle || "\u00A0"}</p>
                        </header>
                        <div className="admin-auth-form-body">{children}</div>
                    </div>

                    <footer className="admin-auth-footer">
                        <p className="admin-auth-version font-mono">console · privileged session</p>
                        <Link to={APP_LOGIN} className="admin-auth-back-link">
                            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                            Workspace login
                        </Link>
                    </footer>
                </div>
            </div>
        </div>
    );
}

/**
 * Premium split-screen auth shell — renders as a top-level route (no AppLayout chrome).
 * Admin variant uses a full-viewport single-panel console layout.
 */
export function AuthLayout({ title, subtitle, children, wide = false, variant = "app", loginPage = false }) {
    const isAdmin = variant === "admin";

    if (isAdmin) {
        return (
            <AdminAuthShell title={title} subtitle={subtitle} loginPage={loginPage}>
                {children}
            </AdminAuthShell>
        );
    }

    return (
        <div
            className={`auth-shell auth-route-root${loginPage ? " auth-route-login" : ""}`}
            data-testid="auth-shell"
        >
            <SiteAnnouncementBar audience="auth" />
            <AdminPortalEasterEgg />
            <aside className="auth-shell__marketing">
                <div className="auth-shell__marketing-grid grid-bg" aria-hidden />
                <div className="auth-shell__marketing-glow" aria-hidden />
                <div className="auth-shell__marketing-inner">
                    <AppMarketingPanel />
                </div>
            </aside>

            <div className="auth-shell__form-panel">
                <div className={`auth-shell__form ${wide ? "auth-shell__form--wide" : ""}`}>
                    <div className="auth-shell__mobile-wheel lg:hidden">
                        <AuthWheelShowcase compact />
                    </div>

                    <div className="auth-shell__mobile-brand">
                        <BrandMark variant="app" compact />
                    </div>

                    <header className="auth-shell__header">
                        <h1 className="auth-shell__title">{title}</h1>
                        <p className="auth-shell__subtitle">{subtitle || "\u00A0"}</p>
                    </header>

                    <div className="auth-shell__form-body">{children}</div>
                    <SiteFooter variant="compact" className="mt-6 pb-4" />
                </div>
            </div>
        </div>
    );
}

/** @deprecated Use AuthLayout */
export const LoginLayout = AuthLayout;
